import React, { useEffect, useState } from 'react';
import { useSession } from '../context/Session';
import { useT } from '../i18n';
import { Icon, initials, useConfirm, useToast } from '../components/ui';
import { BusyButton } from '../components/nutrition';
import { connectTraineeToTrainer, disconnectTraineeFromTrainer, getTrainerEmailFromCode, updateUser } from '../lib/services';

function PersonalInfo({ user }) {
  const t = useT();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({});
  useEffect(() => {
    if (!editing) {
      setF({ firstName: user.firstName, lastName: user.lastName, age: user.age ?? '', height: user.heightCm ?? '', fitnessLevel: user.fitnessLevel || '' });
    }
  }, [user, editing]);

  const save = async () => {
    if (!f.firstName.trim()) return toast.warning(t('pleaseEnterYourFirstName'));
    if (!f.lastName.trim()) return toast.warning(t('pleaseEnterYourLastName'));
    if (f.firstName.length > 30) return toast.warning(t('firstNameMustBe30CharactersOrLess'));
    if (f.lastName.length > 30) return toast.warning(t('lastNameMustBe30CharactersOrLess'));
    const age = f.age === '' ? null : parseInt(f.age, 10);
    if (age != null && (Number.isNaN(age) || age < 10 || age > 120)) return toast.warning(t('pleaseEnterAValidAge'));
    const height = f.height === '' ? null : parseInt(f.height, 10);
    if (height != null && (Number.isNaN(height) || height < 100 || height > 250)) return toast.warning(t('macroTargetsInvalidHeight'));
    setBusy(true);
    try {
      await updateUser(user.id, {
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        age,
        height_cm: height,
        fitnessLevel: f.fitnessLevel || null,
      });
      toast.success(t('profileUpdatedSuccessfully'));
      setEditing(false);
    } catch (e) {
      toast.error(t('failedToUpdateProfile'));
    } finally {
      setBusy(false);
    }
    return null;
  };

  const row = (label, value) => (
    <div className="gm-kv"><span>{label}</span><b>{value || t('notSet')}</b></div>
  );

  return (
    <div className="gm-card gm-card--pad-lg">
      <div className="gm-row-between" style={{ marginBottom: 12 }}>
        <div className="gm-card-title">{t('personalInformation')}</div>
        {!editing && <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" onClick={() => setEditing(true)}><Icon name="edit" outline size={18} /> {t('edit')}</button>}
      </div>
      {editing ? (
        <div className="gm-stack">
          <div className="gm-grid-2 keep">
            <div className="gm-field"><label>{t('firstName')}</label><input className="gm-input" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} /></div>
            <div className="gm-field"><label>{t('lastName')}</label><input className="gm-input" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} /></div>
            <div className="gm-field"><label>{t('age')}</label><input className="gm-input" inputMode="numeric" value={f.age} onChange={(e) => setF({ ...f, age: e.target.value.replace(/[^0-9]/g, '') })} /></div>
            <div className="gm-field"><label>{t('macroTargetsHeightCm')}</label><input className="gm-input" inputMode="numeric" value={f.height} onChange={(e) => setF({ ...f, height: e.target.value.replace(/[^0-9]/g, '') })} /></div>
          </div>
          <div className="gm-field">
            <label>{t('fitnessLevel')}</label>
            <select className="gm-select" value={f.fitnessLevel} onChange={(e) => setF({ ...f, fitnessLevel: e.target.value })}>
              <option value="">{t('notSet')}</option>
              <option value="beginner">{t('beginner')}</option>
              <option value="intermediate">{t('intermediate')}</option>
              <option value="advanced">{t('advanced')}</option>
            </select>
          </div>
          <div className="gm-row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="gm-btn gm-btn--ghost" onClick={() => setEditing(false)}>{t('cancel')}</button>
            <BusyButton busy={busy} onClick={save}>{t('save')}</BusyButton>
          </div>
        </div>
      ) : (
        <>
          {row(t('firstName'), user.firstName)}
          {row(t('lastName'), user.lastName)}
          {row(t('email'), user.email)}
          {row(t('age'), user.age)}
          {row(t('macroTargetsHeightCm'), user.heightCm)}
          {row(t('fitnessLevel'), user.fitnessLevel ? t(user.fitnessLevel) : null)}
          {row(t('role'), user.isTrainer ? t('trainer') : t('trainee'))}
        </>
      )}
    </div>
  );
}

function TrainerConnection({ user }) {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    if (!/^\d{4}$/.test(code.trim())) {
      toast.warning(t('pleaseEnterAValid4DigitCode'));
      return;
    }
    setBusy(true);
    try {
      const trainerEmail = await getTrainerEmailFromCode(code.trim());
      if (!trainerEmail) {
        toast.error(t('invalidCode'));
        return;
      }
      await connectTraineeToTrainer(user.id, trainerEmail, code.trim());
      setCode('');
      toast.success(t('successfullyConnectedToTrainer'));
    } catch (e) {
      toast.error(e.message === 'MAXIMUM_TRAINEE_LIMIT_REACHED' ? t('maximumTraineeLimitReached') : t('failedToConnect'));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    const ok = await confirm({ title: t('disconnect'), message: user.trainerEmail, confirmLabel: t('disconnect'), danger: true });
    if (!ok) return;
    try {
      await disconnectTraineeFromTrainer(user.id, user.trainerEmail);
      toast.success(t('disconnectedFromTrainer'));
    } catch (e) {
      toast.error(t('failedToDisconnect'));
    }
  };

  return (
    <div className="gm-card gm-card--pad-lg">
      <div className="gm-card-title" style={{ marginBottom: 12 }}>{t('connectToTrainer')}</div>
      {user.trainerEmail ? (
        <div className="gm-row">
          <div className="gm-icon-tile gm-icon-tile--sm"><Icon name="sports" /></div>
          <div className="gm-grow">
            <div className="gm-label">{t('connectedTrainer')}</div>
            <div className="gm-strong">{user.trainerEmail}</div>
          </div>
          <button type="button" className="gm-btn gm-btn--outline gm-btn--sm" onClick={disconnect}>{t('disconnect')}</button>
        </div>
      ) : (
        <>
          <p className="gm-muted gm-small" style={{ marginBottom: 10 }}>{t('noTrainerConnected')}</p>
          <div className="gm-row">
            <input className="gm-input" style={{ maxWidth: 180, letterSpacing: 6, fontWeight: 700, textAlign: 'center' }} inputMode="numeric" maxLength={4} placeholder="0000" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))} />
            <BusyButton busy={busy} onClick={connect}>{t('connect')}</BusyButton>
          </div>
          <span className="gm-tiny gm-muted">{t('enterTrainerCode')}</span>
        </>
      )}
    </div>
  );
}

export default function Profile() {
  const t = useT();
  const { user } = useSession();
  return (
    <div className="gm-content gm-content--narrow gm-enter">
      <div className="gm-page-head"><h1 className="gm-page-title">{t.upper(t('profile'))}</h1></div>
      <div className="gm-card gm-card--pad-lg gm-row" style={{ marginBottom: 16, gap: 16 }}>
        <div className="gm-avatar" style={{ width: 64, height: 64, fontSize: 26, borderRadius: 14, cursor: 'default' }}>{initials(user)}</div>
        <div className="gm-grow">
          <div style={{ fontSize: 20, fontWeight: 800 }}>{`${user.firstName} ${user.lastName}`.trim()}</div>
          <div className="gm-muted gm-small">{user.email}</div>
          <div className="gm-row" style={{ marginTop: 8 }}>
            <span className="gm-pill">{user.isTrainer ? t('trainer') : t('trainee')}</span>
            <span className="gm-pill" style={user.isPremium ? { color: 'var(--gm-premium-b)' } : undefined}>
              {user.subscription === 'super' ? t('superPlan') : user.subscription === 'limitless' ? t('limitlessPlan') : user.isPremium ? t('premiumPlan') : t('freePlan')}
            </span>
          </div>
        </div>
      </div>
      <div className="gm-stack">
        <PersonalInfo user={user} />
        {!user.isTrainer && <TrainerConnection user={user} />}
      </div>
    </div>
  );
}
